import { ChartBarIcon, EyeIcon } from '@heroicons/react/24/outline';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TrackingResponseDto } from '../../types/types';

interface ProfileStatsSectionProps {
    stats: TrackingResponseDto | null;
    displayedProfileViews: number;
    profileRange: '7days' | 'month' | 'all';
    onProfileRangeChange: (range: '7days' | 'month' | 'all') => void;
    selectedChartType: 'profile' | 'card' | null;
    onChartTypeChange: (type: 'profile' | 'card' | null) => void;
    selectedCard: any;
    selectedCardStatsMap: Record<string, TrackingResponseDto>;
    cardRangeMap: Record<string, '7days' | 'month' | 'all'>;
}

const VerticalBarChart: React.FC<{
    data: { country: string; views: number }[];
    title?: string;
    showPercentage?: boolean;
}> = ({ data, title, showPercentage = false }) => {
    const { t } = useTranslation('dashboard');
    const [animatedHeights, setAnimatedHeights] = useState<number[]>([]);

    useEffect(() => {
        if (!data || data.length === 0) return;

        const maxViews = Math.max(...data.map(item => item.views));
        const targetHeights = data.map(item => (item.views / maxViews) * 180);

        let current = new Array(data.length).fill(0);
        const steps = 30;

        const interval = setInterval(() => {
            current = current.map((val, idx) => {
                const increment = targetHeights[idx] / steps;
                const next = val + increment;
                return next >= targetHeights[idx] ? targetHeights[idx] : next;
            });

            setAnimatedHeights([...current]);

            const done = current.every((val, idx) => val >= targetHeights[idx]);
            if (done) clearInterval(interval);
        }, 20);

        return () => clearInterval(interval);
    }, [data]);

    if (!data || data.length === 0) return null;

    const totalViews = data.reduce((sum, item) => sum + item.views, 0);
    const maxViews = Math.max(...data.map(item => item.views));

    return (
        <div className="bg-white p-2 shadow-sm border border-slate-100 rounded-md VerticalBarChart">
            <h4 className="text-sm font-semibold mb-2 text-slate-800 text-center">
                {t(title || '')}
            </h4>

            <div className="max-w-full overflow-x-auto">
                <div className="flex items-end gap-3 h-56 mb-3 min-w-max px-2 justify-center">
                    {data.map((item, index) => {
                        const height = animatedHeights[index] || 0;
                        const percentage = ((item.views / totalViews) * 100).toFixed(1);

                        const getBarColor = (index: number, views: number) => {
                            if (views === maxViews) return 'bg-green-800';
                            if (index === 1) return 'bg-green-600';
                            if (index === 2) return 'bg-green-400';
                            return 'bg-gray-300';
                        };

                        return (
                            <div key={`bar-${index}`} className="flex flex-col items-center">
                                {showPercentage && parseFloat(percentage) > 5 && (
                                    <div className="text-[10px] font-medium text-slate-600 text-center mb-1">
                                        {percentage}%
                                    </div>
                                )}

                                <div className="flex flex-col items-center justify-end" style={{ height: '180px' }}>
                                    <div
                                        className={`w-14 transition-all duration-300 ease-out ${getBarColor(index, item.views)}`}
                                        style={{ height: `${height}px` }}
                                    />
                                </div>

                                <div className="mt-1 text-slate-600 text-[10px] font-medium text-center">
                                    {item.views}
                                </div>

                                <div
                                    className="mt-1 text-[10px] font-medium text-slate-700 text-center max-w-14 truncate"
                                    title={item.country || t('not_specified')}
                                >
                                    {item.country || t('not_specified')}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            <div className="flex justify-between items-center text-[10px] text-slate-500 relative">
                <div className="flex-1 text-center font-medium">
                    {t('countries')}
                </div>
            </div>
        </div>
    );
};

export const ProfileStatsSection: React.FC<ProfileStatsSectionProps> = ({
    stats,
    displayedProfileViews,
    profileRange,
    onProfileRangeChange,
    selectedChartType,
    onChartTypeChange,
    selectedCard,
    selectedCardStatsMap,
    cardRangeMap,
}) => {
    const { t } = useTranslation('dashboard');

    const renderChartContent = () => {
        if (selectedChartType === 'profile' && stats && stats.countryViews.length > 0) {
            return (
                <div className="w-full h-full">
                    <VerticalBarChart
                        data={stats.countryViews}
                        showPercentage={true}
                    />
                </div>
            );
        }

        if (selectedChartType === 'card' && selectedCard) {
            const cardId = 'cardId' in selectedCard ? selectedCard.cardId : selectedCard.id.toString();
            const currentStats = selectedCardStatsMap[cardId];

            return currentStats && currentStats.countryViews.length > 0 ? (
                <div className="w-full h-full">
                    <VerticalBarChart
                        data={currentStats.countryViews}
                        showPercentage={true}
                    />
                </div>
            ) : (
                <div className="flex flex-col items-center justify-center text-center py-8">
                    <div className="w-12 h-12 bg-slate-100 rounded-md flex items-center justify-center mb-2">
                        <EyeIcon className="w-6 h-6 text-slate-400" />
                    </div>
                    <h3 className="text-sm font-semibold text-slate-700 mb-1">
                        {t('no_chart_data')}
                    </h3>
                    <p className="text-xs text-slate-500">
                        {t('no_chart_data_description')}
                    </p>
                </div>
            );
        }

        return (
            <div className="flex flex-col items-center justify-center text-center py-8">
                <div className="w-12 h-12 bg-slate-100 rounded-md flex items-center justify-center mb-2">
                    <ChartBarIcon className="w-6 h-6 text-slate-400" />
                </div>
                <h3 className="text-sm font-semibold text-slate-700 mb-1">
                    {t('select_item_for_chart')}
                </h3>
                <p className="text-xs text-slate-500">
                    {t('click_profile_or_card_to_view_analytics')}
                </p>
            </div>
        );
    };

    return (
        <div className="col-span-3 flex flex-col gap-2">
            {stats && (
                <div
                    onClick={() => {
                        if (selectedChartType !== 'profile') {
                            onChartTypeChange('profile');
                        }
                    }}
                    className="cursor-pointer group"
                >
                    <div className="min-h-[110px] bg-white p-2 text-center shadow-md border border-slate-200 transition-all duration-300 rounded-md">
                        <div className="text-xs mb-1 text-slate-500 font-medium">
                            {t('total_profile_views')}
                        </div>
                        <div className="text-2xl font-bold text-slate-800 mb-1">
                            {displayedProfileViews}
                        </div>
                        <div className="flex flex-row gap-4 text-[10px] text-slate-600 justify-center">
                            {(['7days', 'month', 'all'] as const).map((rangeVal) => (
                                <label
                                    key={rangeVal}
                                    className="flex items-center gap-1 cursor-pointer hover:text-blue-600"
                                >
                                    <input
                                        type="radio"
                                        value={rangeVal}
                                        checked={profileRange === rangeVal}
                                        onChange={() => onProfileRangeChange(rangeVal)}
                                        className="w-2 h-2"
                                    />
                                    <span>
                                        {t(
                                            rangeVal === '7days'
                                                ? 'seven_days'
                                                : rangeVal === 'month'
                                                    ? 'one_month'
                                                    : 'all'
                                        )}
                                    </span>
                                </label>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            <div className="bg-white p-2 shadow-lg border border-slate-200 rounded-md">
                <div className="mb-2">
                    <h3 className="text-sm font-semibold text-slate-800">
                        {selectedChartType === 'profile'
                            ? t('profile_views_by_country') 
                            : selectedCard
                                ? t('card_views_by_country')
                                : t('select_card_to_view_chart')}
                    </h3>
                </div>

                <div className="min-h-[300px] flex items-center justify-center">
                    {renderChartContent()}
                </div>
            </div>
        </div>
    );
};